<template>
  <section class="stats-widget" :class="{ 'stats-widget--span-2': spanFull }">
    <header class="stats-widget__header">
      <div class="stats-widget__title-group">
        <div class="stats-widget__icon" :class="`stats-widget__icon--${tone}`">
          <BaseIcon :name="icon" size="16" />
        </div>
        <div class="stats-widget__heading">
          <h3 class="stats-widget__title">{{ title }}</h3>
          <p v-if="description" class="stats-widget__description">{{ description }}</p>
        </div>
      </div>
      <div class="stats-widget__actions">
        <slot name="actions" />
        <button
          v-if="!hideRefresh"
          type="button"
          class="stats-widget__refresh"
          :class="{ 'stats-widget__refresh--spinning': loading }"
          :title="$t('stats_dashboard.refresh_widget')"
          :aria-label="$t('stats_dashboard.refresh_widget')"
          @click="$emit('refresh')"
        >
          <BaseIcon name="redo" size="12" />
        </button>
      </div>
    </header>

    <div class="stats-widget__body">
      <!-- Error state -->
      <div v-if="error" class="stats-widget__state stats-widget__state--error">
        <div class="stats-widget__state-icon stats-widget__state-icon--error">
          <BaseIcon name="exclamation-triangle" size="20" />
        </div>
        <h4 class="stats-widget__state-title">
          {{ errorTitle }}
        </h4>
        <p class="stats-widget__state-description">{{ errorDescription }}</p>
        <Btn variant="outlined" size="sm" icon="redo" @click="$emit('refresh')">
          {{ $t('stats_dashboard.error.retry') }}
        </Btn>
      </div>

      <!-- Loading skeleton (only on first load — keeps existing data
           visible during refetches for SWR-style UX) -->
      <div v-else-if="loading && !hasData" class="stats-widget__state stats-widget__state--loading">
        <slot name="skeleton">
          <div class="stats-widget__skeleton-block" />
          <div class="stats-widget__skeleton-block stats-widget__skeleton-block--short" />
          <div class="stats-widget__skeleton-block" />
        </slot>
      </div>

      <!-- Empty state -->
      <div v-else-if="!hasData" class="stats-widget__state stats-widget__state--empty">
        <div class="stats-widget__state-icon">
          <BaseIcon :name="emptyIcon || icon" size="20" />
        </div>
        <p class="stats-widget__state-description">
          {{ emptyMessage || $t('stats_dashboard.empty_data') }}
        </p>
      </div>

      <!-- Default content slot -->
      <div v-else class="stats-widget__content">
        <slot />
      </div>
    </div>
  </section>
</template>

<script>
  import { computed, getCurrentInstance } from 'vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import Btn from '@/components/partials/Btn.vue';

  export default {
    name: 'StatsWidget',

    components: { BaseIcon, Btn },

    props: {
      title: { type: String, required: true },
      description: { type: String, default: '' },
      icon: { type: String, default: 'insights' },
      tone: {
        type: String,
        default: 'brand',
        validator: (v) => ['brand', 'green', 'amber', 'sky', 'violet', 'rose', 'slate'].includes(v),
      },
      loading: { type: Boolean, default: false },
      error: { type: Object, default: null },
      hasData: { type: Boolean, default: false },
      emptyIcon: { type: String, default: null },
      emptyMessage: { type: String, default: '' },
      hideRefresh: { type: Boolean, default: false },
      spanFull: { type: Boolean, default: false },
    },

    emits: ['refresh'],

    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;

      const errorTitle = computed(() => {
        if (props.error?.code === 'timeout') {
          return $t('stats_dashboard.error.timeout_title');
        }
        return $t('stats_dashboard.error.failed_title');
      });

      const errorDescription = computed(() => {
        if (props.error?.code === 'timeout') {
          return $t('stats_dashboard.error.timeout_description');
        }
        return $t('stats_dashboard.error.failed_description');
      });

      return { errorTitle, errorDescription };
    },
  };
</script>

<style scoped lang="scss">
  @import '@/assets/scss/base/_mixins.scss';

  .stats-widget {
    background: #ffffff;
    border: 1px solid var(--slate-200);
    border-radius: 0.875rem;
    box-shadow: 0 1px 2px rgb(0 0 0 / 0.04);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    min-height: 220px;

    &--span-2 {
      grid-column: span 2;

      @include lg-down {
        grid-column: span 1;
      }
    }

    &__header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 0.75rem;
      padding: 1rem 1.125rem 0.75rem;
      border-bottom: 1px solid var(--slate-100);
      @include lg-down {
        flex-direction: column;
        align-items: flex-start;
      }
    }

    &__title-group {
      display: flex;
      gap: 0.625rem;
      align-items: flex-start;
      min-width: 0;
    }

    &__icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 30px;
      height: 30px;
      border-radius: 0.5rem;
      flex-shrink: 0;

      &--brand {
        background: var(--brand-50);
        color: var(--brand-600);
      }
      &--green {
        background: var(--green-50);
        color: var(--green-600);
      }
      &--amber {
        background: var(--amber-50);
        color: var(--amber-600);
      }
      &--sky {
        background: var(--sky-50, #f0f9ff);
        color: var(--sky-600, #0284c7);
      }
      &--violet {
        background: var(--violet-50, #f5f3ff);
        color: var(--violet-600, #7c3aed);
      }
      &--rose {
        background: var(--rose-50, #fff1f2);
        color: var(--rose-600, #e11d48);
      }
      &--slate {
        background: var(--slate-100);
        color: var(--slate-600);
      }
    }

    &__heading {
      min-width: 0;
    }

    &__title {
      margin: 0;
      font-size: 0.9375rem;
      font-weight: 600;
      color: var(--slate-900);
      @include ellipsis(1);
      margin-top: 0.375rem;
    }

    &__description {
      margin: 0.125rem 0 0;
      font-size: 0.75rem;
      color: var(--slate-500);
      line-height: 1.35;
    }

    &__actions {
      display: flex;
      align-items: center;
      gap: 0.375rem;
      flex-shrink: 0;
    }

    &__refresh {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      border: 1px solid transparent;
      border-radius: 0.375rem;
      background: transparent;
      color: var(--slate-500);
      cursor: pointer;
      transition:
        background-color 0.15s ease,
        color 0.15s ease,
        border-color 0.15s ease;

      @include hover {
        background: var(--slate-100);
        color: var(--slate-700);
      }

      &--spinning .icon {
        animation: stats-widget-spin 0.9s linear infinite;
      }
    }

    &__body {
      flex: 1;
      padding: 1rem 1.125rem 1.125rem;
      min-height: 0;
      display: flex;
      flex-direction: column;
    }

    &__content {
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;
    }

    &__state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 0.625rem;
      flex: 1;
      padding: 1.5rem 1rem;
      text-align: center;
    }

    &__state-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 44px;
      height: 44px;
      border-radius: 50%;
      background: var(--slate-100);
      color: var(--slate-500);

      &--error {
        background: var(--red-50);
        color: var(--red-500);
      }
    }

    &__state-title {
      margin: 0;
      font-size: 0.9375rem;
      font-weight: 600;
      color: var(--slate-800);
    }

    &__state-description {
      margin: 0;
      max-width: 26rem;
      font-size: 0.8125rem;
      color: var(--slate-500);
      line-height: 1.5;
    }

    &__skeleton-block {
      height: 18px;
      border-radius: 0.375rem;
      background: linear-gradient(
        90deg,
        var(--slate-100) 25%,
        var(--slate-200) 50%,
        var(--slate-100) 75%
      );
      background-size: 200% 100%;
      animation: stats-widget-shimmer 1.5s infinite;

      & + & {
        margin-top: 0.625rem;
      }

      &--short {
        width: 60%;
      }
    }
  }

  @keyframes stats-widget-shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }

  @keyframes stats-widget-spin {
    to {
      transform: rotate(360deg);
    }
  }
</style>
